/**
 * Prefetch Hook for Shopify Products
 * 
 * This hook initiates the Shopify products fetch after initial page load,
 * avoiding the critical render path. This breaks the network dependency
 * chain: HTML → JS → lazy chunk → API call
 * 
 * By deferring the prefetch until after LCP, the products data loads
 * in the background without affecting initial page performance.
 * 
 * IMPORTANT: Uses dynamic imports to keep Shopify/Zod out of the initial bundle.
 */

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Prefetches products data after initial page load.
 * Deferred to avoid extending the critical network chain.
 * Uses dynamic imports to keep bundle size small.
 */
export const usePrefetchProducts = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Prefetch products for home page StorePreviewSection
    const prefetch = async () => {
      // Dynamic import to keep Shopify/Zod out of initial bundle
      const { storefrontApiRequest, STOREFRONT_PRODUCTS_QUERY } = await import("@/lib/shopify");
      
      queryClient.prefetchQuery({
        queryKey: ["shopify-products-preview"],
        queryFn: async () => {
          const result = await storefrontApiRequest(STOREFRONT_PRODUCTS_QUERY, { 
            first: 10,
            sortKey: 'CREATED_AT',
            reverse: true // Newest products first
          });
          const data = result as { data?: { products?: { edges?: Array<{ node: { title: string } }> } } } | null;
          const products = data?.data?.products?.edges ?? [];
          
          // Products are already sorted by creation date (newest first) from API
          return products.slice(0, 3);
        },
        staleTime: 10 * 60 * 1000, // 10 minutes
      });
    };

    // Defer prefetch until AFTER page load event to completely break critical chain
    // This ensures Shopify API call never competes with LCP resources
    const deferPrefetch = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => prefetch(), { timeout: 8000 });
      } else {
        setTimeout(prefetch, 5000);
      }
    };

    // Wait for page load + additional delay to ensure LCP has completed
    const handleLoad = () => {
      // Extra 2s buffer after load to ensure LCP is complete
      setTimeout(deferPrefetch, 2000);
    };

    // If page already loaded, start timer immediately
    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad, { once: true });
    }

    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [queryClient]);
};
